import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { TAG } from '@/constants';

// admin api
const ADMIN_URL = 'admin/api/v1';

export interface CalculatorRateCard {
  _id: string;
  country: string;
  channel: string;
  publisher: string;
  format?: string;
  buyCpm: number;
  sellCpm?: number;
  currency: string;
}

export type CalculatorRateCardInput = {
  country: string;
  channel: string;
  publisher: string;
  format?: string;
  buyCpm: number;
  sellCpm?: number | null;
  currency: string;
};

export interface CalculatorRateCardsResponse {
  country?: string;
  rateCards: CalculatorRateCard[];
  count: number;
}

export interface TradingDeal {
  _id: string;
  name: string;
  percentage: number;
  isActive: boolean;
  /**
   * This buying point books only against a client order it signs itself — a
   * Prisma, or a DocuSign, which is the same process under another name.
   *
   * Raising an IO picks this up, so its line items are held back from the
   * booking database until the signed order is attached.
   */
  requiresClientOrder?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface TradingDealsResponse {
  tradingDeals: TradingDeal[];
  count: number;
}

export interface TradingDealChannel {
  _id: string;
  name: string;
  channel: string;
  percentage: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface TradingDealChannelsResponse {
  tradingDealChannels: TradingDealChannel[];
  count: number;
}

export interface DspFee {
  _id: string;
  dspName: string;
  feePercentage: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface DspFeesResponse {
  dspFees: DspFee[];
  count: number;
}

/** Defaults a new line item takes when this channel is picked; set in Trader Controls. */
export interface ChannelLineItemDefaults {
  /** Prefilled into the line's targeting details. */
  defaultTargetingDetails?: string;
  /** DSP (a DSP fee's `dspName`) preselected on the line. */
  defaultDsp?: string;
}

export interface AdminChannel extends ChannelLineItemDefaults {
  _id: string;
  key: string;
  label: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminChannelsResponse {
  channels: AdminChannel[];
  count: number;
}

export interface AdminKpi {
  _id: string;
  kpi: string;
  benchmark: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminKpisResponse {
  kpis: AdminKpi[];
  count: number;
}

// Define a service using a base URL and expected endpoints
export const adminApi = createApi({
  reducerPath: 'adminApi',
  tagTypes: [TAG.ADMIN],
  baseQuery,
  endpoints: (builder) => ({
    getCalculatorRateCards: builder.query<CalculatorRateCardsResponse, { country?: string }>({
      query: ({ country }) => {
        const url = `${ADMIN_URL}/calculator-rate-cards`;
        return country ? `${url}?country=${encodeURIComponent(country)}` : url;
      },
      providesTags: () => [TAG.ADMIN]
    }),
    createCalculatorRateCard: builder.mutation<CalculatorRateCard, CalculatorRateCardInput>({
      query: (body) => ({
        url: `${ADMIN_URL}/calculator-rate-cards`,
        method: 'POST',
        body
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    updateCalculatorRateCard: builder.mutation<
      CalculatorRateCard,
      { id: string; body: Partial<CalculatorRateCardInput> }
    >({
      query: ({ id, body }) => ({
        url: `${ADMIN_URL}/calculator-rate-cards/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    deleteCalculatorRateCard: builder.mutation<CalculatorRateCard, string>({
      query: (id) => ({
        url: `${ADMIN_URL}/calculator-rate-cards/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    getTradingDeals: builder.query<TradingDealsResponse, { includeInactive?: boolean } | void>({
      query: (params) =>
        params && typeof params === 'object' && params.includeInactive
          ? `${ADMIN_URL}/trading-deals?includeInactive=true`
          : `${ADMIN_URL}/trading-deals`,
      providesTags: () => [TAG.ADMIN]
    }),
    getTradingDealChannels: builder.query<TradingDealChannelsResponse, void>({
      query: () => `${ADMIN_URL}/trading-deal-channels`,
      providesTags: () => [TAG.ADMIN]
    }),
    createAdminTradingDeal: builder.mutation<
      TradingDeal,
      { name: string; percentage: number; requiresClientOrder?: boolean }
    >({
      query: (body) => ({
        url: `${ADMIN_URL}/trading-deals`,
        method: 'POST',
        body
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    updateAdminTradingDeal: builder.mutation<
      TradingDeal,
      {
        id: string;
        body: Partial<
          Pick<TradingDeal, 'name' | 'percentage' | 'isActive' | 'requiresClientOrder'>
        >;
      }
    >({
      query: ({ id, body }) => ({
        url: `${ADMIN_URL}/trading-deals/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    deleteAdminTradingDeal: builder.mutation<TradingDeal, string>({
      query: (id) => ({
        url: `${ADMIN_URL}/trading-deals/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    createAdminTradingDealChannel: builder.mutation<
      TradingDealChannel,
      { name: string; channel: string; percentage: number }
    >({
      query: (body) => ({
        url: `${ADMIN_URL}/trading-deal-channels`,
        method: 'POST',
        body
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    updateAdminTradingDealChannel: builder.mutation<
      TradingDealChannel,
      { id: string; body: Partial<Pick<TradingDealChannel, 'name' | 'channel' | 'percentage'>> }
    >({
      query: ({ id, body }) => ({
        url: `${ADMIN_URL}/trading-deal-channels/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    deleteAdminTradingDealChannel: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `${ADMIN_URL}/trading-deal-channels/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    getDspFees: builder.query<DspFeesResponse, { includeInactive?: boolean } | void>({
      query: (params) =>
        params && typeof params === 'object' && 'includeInactive' in params && params.includeInactive
          ? `${ADMIN_URL}/dsp-fees?includeInactive=true`
          : `${ADMIN_URL}/dsp-fees`,
      providesTags: () => [TAG.ADMIN]
    }),
    getAdminChannels: builder.query<AdminChannelsResponse, { includeInactive?: boolean }>({
      query: ({ includeInactive }) =>
        includeInactive ? `${ADMIN_URL}/channels?includeInactive=true` : `${ADMIN_URL}/channels`,
      providesTags: () => [TAG.ADMIN]
    }),
    createAdminChannel: builder.mutation<AdminChannel, { key: string; label: string } & ChannelLineItemDefaults>({
      query: (body) => ({
        url: `${ADMIN_URL}/channels`,
        method: 'POST',
        body
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    updateAdminChannel: builder.mutation<
      AdminChannel,
      /* A blank default clears it on the server. */
      { id: string; body: Partial<Pick<AdminChannel, 'key' | 'label' | 'isActive'> & ChannelLineItemDefaults> }
    >({
      query: ({ id, body }) => ({
        url: `${ADMIN_URL}/channels/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    deleteAdminChannel: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `${ADMIN_URL}/channels/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    seedAdminChannelsFromRateCards: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: `${ADMIN_URL}/channels/seed-from-rate-cards`,
        method: 'POST'
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    getAdminDspFees: builder.query<DspFeesResponse, { includeInactive?: boolean }>({
      query: ({ includeInactive }) =>
        includeInactive ? `${ADMIN_URL}/dsp-fees?includeInactive=true` : `${ADMIN_URL}/dsp-fees`,
      providesTags: () => [TAG.ADMIN]
    }),
    createAdminDspFee: builder.mutation<DspFee, { dspName: string; feePercentage: number }>({
      query: (body) => ({
        url: `${ADMIN_URL}/dsp-fees`,
        method: 'POST',
        body
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    updateAdminDspFee: builder.mutation<DspFee, { id: string; body: Partial<Pick<DspFee, 'dspName' | 'feePercentage' | 'isActive'>> }>({
      query: ({ id, body }) => ({
        url: `${ADMIN_URL}/dsp-fees/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    deleteAdminDspFee: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `${ADMIN_URL}/dsp-fees/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    getAdminKpis: builder.query<AdminKpisResponse, { includeInactive?: boolean }>({
      query: ({ includeInactive }) =>
        includeInactive ? `${ADMIN_URL}/kpis?includeInactive=true` : `${ADMIN_URL}/kpis`,
      providesTags: () => [TAG.ADMIN]
    }),
    createAdminKpi: builder.mutation<AdminKpi, { kpi: string; benchmark: number }>({
      query: (body) => ({
        url: `${ADMIN_URL}/kpis`,
        method: 'POST',
        body
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    updateAdminKpi: builder.mutation<
      AdminKpi,
      { id: string; body: Partial<Pick<AdminKpi, 'kpi' | 'benchmark' | 'isActive'>> }
    >({
      query: ({ id, body }) => ({
        url: `${ADMIN_URL}/kpis/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    deleteAdminKpi: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `${ADMIN_URL}/kpis/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [TAG.ADMIN]
    }),
    seedAdminKpisFromRateCards: builder.mutation<{ success?: boolean; count?: number }, void>({
      query: () => ({
        url: `${ADMIN_URL}/kpis/seed-from-rate-cards`,
        method: 'POST'
      }),
      invalidatesTags: [TAG.ADMIN]
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCalculatorRateCardsQuery,
  useCreateCalculatorRateCardMutation,
  useUpdateCalculatorRateCardMutation,
  useDeleteCalculatorRateCardMutation,
  useGetTradingDealsQuery,
  useGetTradingDealChannelsQuery,
  useCreateAdminTradingDealMutation,
  useUpdateAdminTradingDealMutation,
  useDeleteAdminTradingDealMutation,
  useCreateAdminTradingDealChannelMutation,
  useUpdateAdminTradingDealChannelMutation,
  useDeleteAdminTradingDealChannelMutation,
  useGetDspFeesQuery,
  useGetAdminChannelsQuery,
  useCreateAdminChannelMutation,
  useUpdateAdminChannelMutation,
  useDeleteAdminChannelMutation,
  useSeedAdminChannelsFromRateCardsMutation,
  useGetAdminDspFeesQuery,
  useCreateAdminDspFeeMutation,
  useUpdateAdminDspFeeMutation,
  useDeleteAdminDspFeeMutation,
  useGetAdminKpisQuery,
  useCreateAdminKpiMutation,
  useUpdateAdminKpiMutation,
  useDeleteAdminKpiMutation,
  useSeedAdminKpisFromRateCardsMutation
} = adminApi;
